import React, { type FC } from 'react';

import Avatar from '@atlaskit/avatar';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, xcss, Text } from '@atlaskit/primitives';
import { IconTile } from '@atlaskit/icon';

import { ExternalCollaboratorUserLozenge } from '@confluence/external-collab-ui/entry-points/ExternalUserLozengeLoader.ts';
import { fg } from '@confluence/feature-gating';

import { PRINCIPAL_TYPE } from '../constants.ts';

import { GroupCountComponent } from './GroupCountComponent.tsx';

const lozengeStyles = xcss({
	paddingLeft: 'space.100',
});

const groupDetailsStyle = xcss({
	overflowWrap: 'anywhere',
});

const iconContainer = xcss({
	borderRadius: 'radius.full',
	width: '36px',
	height: '36px',
	backgroundColor: 'color.background.neutral',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	flexShrink: 0,
	marginTop: 'space.0',
});

export type AccessDetailsPrincipalProps = {
	id?: string | null;
	type: PRINCIPAL_TYPE;
	isGuest: boolean;
	displayName?: string | null;
	secondLine?: string | null;
	avatarUrl?: string;
	verified?: boolean;
};

export const AccessDetailsPrincipal: FC<AccessDetailsPrincipalProps> = ({
	type,
	id,
	displayName,
	secondLine,
	avatarUrl,
	isGuest,
	verified,
}) => {
	const groupId = type === PRINCIPAL_TYPE.GROUP ? id : null;
	const teamId = type === PRINCIPAL_TYPE.TEAM ? id : null;

	if (type === 'user') {
		return (
			<Inline space="space.150" alignBlock="center">
				<Avatar src={avatarUrl} appearance="circle" size="medium" label="" />
				<Stack>
					<Stack>
						<Inline>
							<Text color="color.text">{displayName}</Text>
							{isGuest && (
								<Box xcss={lozengeStyles}>
									<ExternalCollaboratorUserLozenge />
								</Box>
							)}
						</Inline>
						<Text size="small" color="color.text.subtlest">
							{secondLine}
						</Text>
					</Stack>
				</Stack>
			</Inline>
		);
	} else if (type === 'team' && fg('confluence_frontend_perms_exp_teams_as_principals')) {
		return (
			<Inline space="space.150" alignBlock="center" testId="team-principal" grow="fill">
				<Avatar src={avatarUrl} appearance="square" size="medium" label="" />
				<Stack>
					<Stack>
						<Inline>
							<Text color="color.text">{displayName}</Text>
						</Inline>
						<Inline grow="fill">
							<GroupCountComponent groupId={teamId} isGroup={false} isVerified={verified} />
						</Inline>
					</Stack>
				</Stack>
			</Inline>
		);
	} else {
		return (
			<Inline space="space.150" alignBlock="center" testId="group-principal" grow="fill">
				<Box xcss={iconContainer}>
					<IconTile label="" size="32" appearance="gray" shape="circle" icon={PeopleGroupIcon} />
				</Box>
				<Stack xcss={groupDetailsStyle}>
					<Inline>{displayName}</Inline>
					<Inline grow="fill">
						<GroupCountComponent groupId={groupId} isGroup={type === 'group'} isVerified={false} />
					</Inline>
				</Stack>
			</Inline>
		);
	}
};
